import _                    from 'underscore';
import React, { ReactNode } from 'react';
import moment               from 'moment';
import { List, ListProps }  from 'semantic-ui-react';

import { CorvalAuditEntry, CorvalSubObjective, EntityAuditDataChangeEntry, EntityWithAuditEntries, OneOrArrayOf, Uuid } from '../types';

import { AUDIT_RECORD_ACTIONS, AUDIT_RECORD_TYPE } from '../constants/SystemRecordEnums';
import { PROJECT_MILESTONE_INFO }   from '../constants/ProductEnums';
import { LangService, TimeService } from '../services';
import { EMPTY_VALUE_DISPLAY }      from '../constants/FormatStringEnums';
import { AuditRecordActionText }                   from '../views/pages/admin/auditing/AuditRecordActionText';

export interface IAuditRecordTypeQuery {
	records: CorvalAuditEntry[],
	types?: AUDIT_RECORD_TYPE[],
	timeBoundary?: 'newest' | 'oldest',
	entityIds?: Uuid[],
	accountIds?: Uuid[],
}

type ChangeEntryExtractionMethod = ( entry: OneOrArrayOf<EntityAuditDataChangeEntry>, ...args: any[] ) => [
	currentValue: any,
	previousValue: any
];

type MapEntityAttributeToChangeEntry<D> = ( attribute: keyof D, changeEntry: EntityAuditDataChangeEntry, ...args: any[] ) => [
	/* The label for this attribute */
	propertyLabel: string,
	
	/* The current value of the attribute, or an array with the value(s) and any decorated versions therein */
	currentValue: any | [ value: any, decoratedValue: ReactNode ],
	
	/* The previous value of the attribute, or an array with the value(s) and any decorated versions therein */
	previousValue: any | [ value: any, decoratedValue: ReactNode ],
];

export interface useAuditRecordsHook {
	
	/* Group audit records by date */
	groupAuditRecordsByDate( auditRecords: CorvalAuditEntry[] ): Record<string, CorvalAuditEntry[]>,
	
	/* Extracts audit records from an entity */
	getAuditRecordsFromEntity( entity: EntityWithAuditEntries ): CorvalAuditEntry[],
	
	/* Extracts audit records from a collection of entities */
	getAuditRecordsFromCollection( collection: Array<{} & EntityWithAuditEntries>, groupBy?: keyof CorvalAuditEntry ): Record<Uuid, CorvalAuditEntry[]>,
	
	/* Returns a filtered audit record set based on criteria given a collection of audit records */
	getAuditRecordTypes: (
		records: IAuditRecordTypeQuery['records'],
		types: IAuditRecordTypeQuery['types'],
		timeBoundary?: IAuditRecordTypeQuery['timeBoundary'],
		entityIds?: Uuid[],
		accountIds?: Uuid[],
	) => CorvalAuditEntry[],
	
	/* Pulls out the value of a change entry for the old / new entry parts */
	extractValueFromChangeEntry: ChangeEntryExtractionMethod,
	
	/* Pulls out the readable version of a change entry for the old / new entry parts */
	extractReadableValueFromChangeEntry: ChangeEntryExtractionMethod,
	
	/* Pulls out the value / readable parts of a change entry comprised of multiple old / new entry parts */
	extractReadableChangeSetFromChangeEntry: ChangeEntryExtractionMethod,
	
	/* Maps an attribute of a subobjective to the correct display for an audit change record performed on that attribute */
	mapObjectiveAttributeToChangeEntry: MapEntityAttributeToChangeEntry<CorvalSubObjective>,
	
	/* Compares two values froma change entry to tell if they are different. Useful when an auditData set has multiple keys on it. */
	valuesFromChangeEntryDiffer( changeSet: [ newValue: any, oldValue: any ] ): boolean,
}

export const getAuditRecordTypes: useAuditRecordsHook['getAuditRecordTypes'] = ( auditEntries = [], types = [], boundary = null, entityIds = null, accountIds = null ) => {
	
	let records = _.filter( auditEntries, record => {
		
		let hasType      = _.isEmpty( types ) ? true : _.isArray( types ) ? types.includes( record.auditType as AUDIT_RECORD_TYPE ) : false,
		    hasEntityId  = _.isArray( entityIds ) ? entityIds.includes( record.entityId ) : true,
		    hasAccountId = _.isArray( accountIds ) ? accountIds.includes( record.accountId ) : true;
		
		return ( hasType && hasEntityId && hasAccountId )
	} );
	
	if( boundary ){
		
		records = _.chain( records )
		           .groupBy( 'auditType' )
		           .mapObject( typeSet => {
			
			           let sortedTypeSet = _.sortBy( typeSet, 'timestampUtc' );
			
			           return ( boundary == 'newest' )
			                  ? _.last( sortedTypeSet )
			                  : _.first( sortedTypeSet );
		           } )
		           .values()
		           .flatten()
		           .value();
	}
	
	return records;
	
}

export const groupAuditRecordsByDate = ( auditRecords: CorvalAuditEntry[] ) => {
	
	return _.groupBy( auditRecords, record => TimeService.ISOtoDisplayDate( record?.timestampUtc ?? TimeService.momentToISO( moment() ) ) );
}

export const getAuditRecordsFromEntity: useAuditRecordsHook['getAuditRecordsFromEntity'] = ( entity ) => {
	
	return entity?.auditEntries ?? [];
}

export const getAuditRecordsFromCollection: useAuditRecordsHook['getAuditRecordsFromCollection'] = ( collection, groupBy = 'entityId' ) => {
	
	return _.chain( collection )
	        .map( ( item ) => getAuditRecordsFromEntity( item ) )
	        .flatten()
	        .compact()
	        .groupBy( groupBy )
	        .value();
}

export const extractValueFromChangeEntry: useAuditRecordsHook['extractValueFromChangeEntry'] = ( entry: EntityAuditDataChangeEntry ) => {
	
	return [
		entry.NewValue?.Value ?? entry.NewValue,
		entry.OldValue?.Value ?? entry.OldValue,
	]
}

export const valuesFromChangeEntryDiffer: useAuditRecordsHook['valuesFromChangeEntryDiffer'] = ( changeSet: [ newValue: any, oldValue: any ] ) => {
	
	let [ newValue, oldValue ] = changeSet;
	
	return ( newValue !== oldValue );
}

export const extractEnumValueFromChangeEntry: useAuditRecordsHook['extractValueFromChangeEntry'] = (
	entry: EntityAuditDataChangeEntry,
	keyValueDictionary: Record<string, string> | 'projectMilestones' = {}
) => {
	
	let [ newValue, oldValue ] = extractReadableValueFromChangeEntry( entry ).map( s => s?.toUpperCase() ?? s ),
	    dictionaries           = {
		    projectMilestones: _.object(
			    _.keys( PROJECT_MILESTONE_INFO ).map( key => key.toUpperCase() ),
			    _.values( PROJECT_MILESTONE_INFO ).map( i => LangService.getTermFromDictionary( i.LABEL ) )
		    )
	    };
	
	let enumDictionary = _.isObject( keyValueDictionary )
	                     ? keyValueDictionary
	                     : _.isString( keyValueDictionary )
	                       ? dictionaries[ keyValueDictionary ] ?? {}
	                       : {};
	
	newValue = enumDictionary[ newValue ] ?? newValue;
	oldValue = enumDictionary[ oldValue ] ?? oldValue;
	
	return [ newValue, oldValue ];
}

export const extractReadableValueFromChangeEntry: useAuditRecordsHook['extractReadableValueFromChangeEntry'] = ( entry: EntityAuditDataChangeEntry ) => {
	return [
		entry.NewValue.Readable,
		entry.OldValue.Readable,
	]
}

export const extractReadableChangeSetFromChangeEntry: useAuditRecordsHook['extractReadableChangeSetFromChangeEntry'] = ( entries: EntityAuditDataChangeEntry[] ) => {
	
	let currentValues  = [],
	    previousValues = [];
	
	// Iterate over everything
	entries.map( ( changeItem: EntityAuditDataChangeEntry ) => {
		
		let actionType = changeItem.ActionType,
		    newValue   = changeItem.NewValue.Readable,
		    oldValue   = changeItem.OldValue.Readable;
		
		switch( actionType ){
			
			case AUDIT_RECORD_ACTIONS.ADD:
				previousValues.push( `--` );
				currentValues.push( <><AuditRecordActionText action={ actionType } label={ 'Added' }/> "{ newValue }"</> );
				break;
			
			case AUDIT_RECORD_ACTIONS.CHANGE:
				previousValues.push( `Previously was "${ oldValue }"` );
				currentValues.push( <><AuditRecordActionText action={ actionType } label={ 'Updated' }/> item to "{ newValue }"</> );
				break;
			
			case AUDIT_RECORD_ACTIONS.REMOVE:
				previousValues.push( `Included as "${ oldValue }"` );
				currentValues.push( <><AuditRecordActionText action={ actionType } label={ 'Removed' }/> "{ oldValue }"</> );
				break;
			
			default:
				previousValues.push( oldValue );
				currentValues.push( newValue );
				break;
		}
	} );
	
	return [ currentValues, previousValues ];
}

export const generateChangeEntryListNode = ( entries: any[], listProps: ListProps = {} ) => {
	
	return (
		<List className={ 'no-padding' } { ...listProps }>
			{ entries.map( ( entry: any, n ) => <List.Item key={ n }>{ entry }</List.Item> ) }
		</List>
	)
}

export const mapObjectiveAttributeToChangeEntry: useAuditRecordsHook['mapObjectiveAttributeToChangeEntry'] = ( attribute, changeEntry, extractDecorated = false ) => {
	
	let propertyLabel = attribute as string,
	    currentValue  = changeEntry as any,
	    previousValue = changeEntry as any;
	
	// console.log( `[AUDIT LOG]: mapping Objective.${ propertyLabel }` );
	
	try{
		
		switch( propertyLabel ){
			
			case 'ProjectTimingMilestone':
				propertyLabel                   = 'Timing Milestone';
				[ currentValue, previousValue ] = extractEnumValueFromChangeEntry( changeEntry, 'projectMilestones' );
				break;
			
			case 'ProjectTimingMilestoneOffsetMonths':
				propertyLabel                   = 'Milestone Offset';
				[ currentValue, previousValue ] = extractValueFromChangeEntry( changeEntry ).map( v => `${ v } ${ ( v !== 0 ) && ( v > 1 || v < -1 ) ? 'Months' : 'Month' }` );
				break;
			
			case 'Conditions':
				propertyLabel                   = 'Criteria';
				[ currentValue, previousValue ] = extractReadableChangeSetFromChangeEntry( changeEntry );
				currentValue                    = [ currentValue, generateChangeEntryListNode( currentValue, { bulleted: currentValue.length > 1 } ) ];
				previousValue                   = [ previousValue, generateChangeEntryListNode( previousValue, { bulleted: previousValue.length > 1 } ) ];
				break;
			
			case 'TargetIntakeQuestionId':
				propertyLabel                   = 'Intake Question';
				[ currentValue, previousValue ] = extractReadableValueFromChangeEntry( changeEntry );
				break;
			
			case 'Ordinal':
				propertyLabel                   = 'Display Order';
				[ currentValue, previousValue ] = extractValueFromChangeEntry( changeEntry ).map( v => `Position ${ v + 1 }` );
				break;
			
			case 'ObjectiveId':
				propertyLabel                   = 'Objective Group';
				[ currentValue, previousValue ] = extractReadableValueFromChangeEntry( changeEntry );
				break;
			
			case 'SubCategoryId':
				propertyLabel                   = 'Subcategory';
				[ currentValue, previousValue ] = extractReadableValueFromChangeEntry( changeEntry );
				break;
			
			case 'CategoryId':
				propertyLabel                   = 'Category';
				[ currentValue, previousValue ] = extractReadableValueFromChangeEntry( changeEntry );
				break;
			
			case 'ApplicableStartYear':
				propertyLabel                   = 'Relative Start Year';
				[ currentValue, previousValue ] = extractReadableValueFromChangeEntry( changeEntry );
				break;
			
			case 'DisplayName':
				propertyLabel                   = 'Display Name';
				[ currentValue, previousValue ] = extractValueFromChangeEntry( changeEntry );
				break;
			
			default:
				[ currentValue, previousValue ] = extractValueFromChangeEntry( changeEntry );
				break;
		}
		
		// if resulting value is an array with raw and decorated versions, choose the decorated one
		if( extractDecorated ){
			currentValue  = _.isArray( currentValue ) ? _.last( currentValue ) : currentValue;
			previousValue = _.isArray( previousValue ) ? _.last( previousValue ) : previousValue;
		}
		
	} catch( e ) {
		
		throw new Error( e.message );
	}
	
	return [
		propertyLabel,
		currentValue ?? EMPTY_VALUE_DISPLAY,
		previousValue ?? EMPTY_VALUE_DISPLAY,
	]
}

export const useAuditRecords = (): useAuditRecordsHook => {
	
	return {
		groupAuditRecordsByDate,
		getAuditRecordTypes,
		getAuditRecordsFromEntity,
		getAuditRecordsFromCollection,
		valuesFromChangeEntryDiffer,
		extractValueFromChangeEntry,
		extractReadableValueFromChangeEntry,
		extractReadableChangeSetFromChangeEntry,
		mapObjectiveAttributeToChangeEntry,
	};
};
